import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import validator from "validator";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getBaseUrl() {
  // Client
  if (typeof window !== "undefined") {
    return new URL("/", window.location.origin).toString();
  }

  // Server and SSG
  if (process.env.NEXT_PUBLIC_URL) {
    return process.env.NEXT_PUBLIC_URL;
  }

  // Else
  if (process.env.VERCEL_URL)
    // reference for vercel.com
    return `https://${process.env.VERCEL_URL}`;
  if (process.env.RENDER_INTERNAL_HOSTNAME)
    // reference for render.com
    return `http://${process.env.RENDER_INTERNAL_HOSTNAME}:${process.env.PORT}`;
  // assume localhost
  return `http://localhost:${process.env.PORT ?? 3000}`;
}

export function getSafeRequestServerUrl() {
  return (
    process.env.NEXT_PUBLIC_SAFE_REQUEST_SERVICE_URL || "http://localhost:8081"
  );
}

export function getSearchSchedulerServerUrl() {
  return (
    process.env.NEXT_PUBLIC_SEARCH_SCHEDULER_SERVICE_URL ||
    "http://localhost:8080"
  );
}

/**
 * Returns the first character of the first name and last name from a name.
 * @param name
 * @returns
 */
export function getInitials(name: string) {
  if (!name) return "";
  const [firstName, lastName] = name.split(" ");
  return `${firstName ? firstName[0].toUpperCase() : ""}${
    lastName ? lastName[0].toUpperCase() : ""
  }`;
}

/**
 * Delays the execution of code by the specified number of milliseconds.
 *
 * @param ms - The number of milliseconds to delay the execution.
 * @returns A Promise that resolves after the specified delay.
 */
export function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function toPascalCase(input: string): string {
  const words = input.split(" ");

  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");
}

export function truthyFilter<T>(
  x: T | false | undefined | null | "" | 0,
): x is T {
  return !!x;
}

export function getFullNameFromFirstLast(
  firstName?: string | null,
  lastName?: string | null,
) {
  return `${firstName || ""} ${lastName || ""}`.trim();
}

/*
 * Given a string of text, extracts all strings that match the email regex. Returns email addresses in lowercase.
 *
 * @param text - The text to search for emails addresses in
 */
export function extractEmailAddressesFromText(text: string): string[] {
  const textLowerCase = text.toLowerCase();
  const emails = textLowerCase.match(/[\w.\-+_]+@[\w.\-+_]+\.[\w]+/g) || [];
  return emails;
}

/**
 *
 * @param str Function to encode a string to a valid uri. Checks if the string is already encoded and returns the same string if it is.
 * @returns encoded string
 */

export function encodeUrl(str: string): string {
  if (str) {
    const decodedStr = decodeURIComponent(str);
    if (str !== decodedStr) {
      // This means that the raw str was already encoded
      return str;
    } else {
      return encodeURIComponent(str);
    }
  }

  return str;
}

/**
 * This function checks whether the lastMessageSentAt is more than 7 days in the past relative to the current date and time.
 *
 * @param lastMessageSentAt MessageThread's last message sent at
 * @param isActionable Whether the conversation is actionable. If false, function will return false regardless of the lastMessageSentAt
 * @returns True if the time difference between the lastMessageSentAt and the current date is greater than 7 days
 */
export function needsFollowUp(
  lastMessageSentAt?: Date,
  isActionable?: boolean,
) {
  if (!lastMessageSentAt) return false;
  if (!isActionable) return false;
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  return new Date(lastMessageSentAt) < oneWeekAgo;
}

/**
 * Takes in an array and a chunk size and returns an array of arrays of the given size.
 * @param array Array to chunk.
 * @param size Size of each chunk.
 * @returns Array of arrays of the given size.
 *
 **/
export function chunkArray<T>(array: T[], size: number) {
  return array.reduce(
    (acc, _, i) => (i % size ? acc : [...acc, array.slice(i, i + size)]),
    [] as T[][],
  );
}

// 'john doe' => "John Doe"
export function capitalizeName(name: string) {
  if (!name) return "";
  const parts = name.split(" ");
  return parts
    .map((part) => {
      if (part.length === 0) return "";
      if (part.length === 1) return part[0].toUpperCase();
      return part[0].toUpperCase() + part.slice(1);
    })
    .join(" ");
}

export function isValidEmail(email: string): boolean {
  if (validator.isEmail(email)) {
    return true;
  } else {
    return false;
  }
}

export function splitName(fullName: string) {
  if (!fullName) return { firstName: "", lastName: "" };
  const nameParts = fullName.split(" ");
  return {
    firstName: nameParts[0],
    lastName: nameParts.slice(1).join(" "),
  };
}

export function stringEqualsAndNotEmpty(a?: string, b?: string): boolean {
  if (!a || !b) {
    return false;
  }
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function zip<T, U>(arr1: T[], arr2: U[]): [T, U][] {
  const result: [T, U][] = [];
  const minLength = Math.min(arr1.length, arr2.length);

  for (let i = 0; i < minLength; i++) {
    result.push([arr1[i], arr2[i]]);
  }

  return result;
}

/**
 * Taken from https://www.npmjs.com/package/is-string-blank?activeTab=code
 *
 * Annoying but is efficient and reliable
 * @param {string} str - The string to check
 * @returns {boolean} Whether the string is blank
 */
export function isStringBlank(str: string): boolean {
  let l = str.length,
    a;
  for (let i = 0; i < l; i++) {
    a = str.charCodeAt(i);
    if (
      (a < 9 || a > 13) &&
      a !== 32 &&
      a !== 133 &&
      a !== 160 &&
      a !== 5760 &&
      a !== 6158 &&
      (a < 8192 || a > 8205) &&
      a !== 8232 &&
      a !== 8233 &&
      a !== 8239 &&
      a !== 8287 &&
      a !== 8288 &&
      a !== 12288 &&
      a !== 65279
    ) {
      return false;
    }
  }
  return true;
}

export function isNumeric(n: any): boolean {
  let type = typeof n;
  if (type === "string") {
    let original = n;
    n = +n;
    // whitespace strings cast to zero - filter them out
    if (n === 0 && isStringBlank(original)) {
      return false;
    }
  } else if (type !== "number") return false;

  return n - n < 1;
}
